import { StyledSelect } from "../../primitives/styled-select";
import * as React from "react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { AlertTriangle, Paperclip, Shield } from "lucide-react";
import { cn } from "../../lib/cn";
import type {
  ActionFieldError,
  ActionFieldSpec,
  ActionFieldValue,
} from "../ActionFieldEditor";
import { EmailComposeSheet } from "../email/EmailComposeSheet";
import type { RichEmailAiEdit } from "../email/RichEmailBody";
import {
  ENVELOPE_CLASSES,
  EnvelopeHeader,
  asRecipientText,
  asString,
  specOfName,
  useEnvelopeCommit,
} from "./envelope-primitives";
import type {
  RefineSubmitInput,
  RefineSubmitResult,
  RefineSubmitWithDiff,
} from "./InlineAIRefiner";

/**
 * PecSendPreview — controlled envelope editor for ``aruba_pec_send`` /
 * ``infocert_pec_send`` proposals (Italian certified email — PEC).
 *
 * Mirrors the controlled render-prop contract of :class:`EmailSendDraftPreview`
 * (the parent ``ActionDrawer`` hands ``schema`` / ``values`` / ``errors`` /
 * ``onChange`` / ``disabled`` and the stacked ``ActionFieldEditor`` skips the
 * fields in :data:`PEC_PREVIEW_OWNED_FIELDS`). Without the controlled API it
 * degrades to a read-only snapshot (post-execution / ``executed`` / ``failed``).
 *
 * PEC carries LEGAL weight — a mis-typed recipient dispatches a certified,
 * legally-binding message to the wrong party and CANNOT be recalled. So the
 * envelope leads with an explicit "valore legale" warning and frames the
 * recipient (``to``) as the highest-risk field, exactly per the recipient-
 * visibility rule that governs the FiC ``schedule_invoice_email`` path.
 *
 * Field keys match the BE wire (:func:`_synth_aruba_pec_send_fields`):
 * ``provider`` / ``from_pec_address`` / ``to`` / ``cc`` / ``subject`` / ``body``.
 */

// ---------------------------------------------------------------------------
// Unified "letter" sheet — ONE bordered sheet with hairline-separated rows and
// a single shared focus-ring (same treatment as the email draft preview). PEC
// keeps its raw mono certified-address inputs (the operator must SEE the exact
// recipient) and its legal-weight banner, rendered INSIDE the sheet. Jul 2026.
// ---------------------------------------------------------------------------

const FIELD_LABEL =
  "w-16 shrink-0 select-none pt-0.5 font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]";
const SHEET_CONTROL =
  "flex-1 bg-transparent text-[12.5px] text-ink-1 placeholder:text-ink-3 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60";
const SHEET_HELP = "px-3 pb-1.5 text-[11px] text-ink-3";
const SHEET_ERR = "px-3 pb-1.5 text-[11px] text-red-600";

/**
 * Plain-text ``body`` → simple email HTML paragraphs — seeds the rich editor
 * from PEC's plain-text ``body`` (PEC carries no ``body_html``: the certified
 * message stays plain text on the wire). Empty → ``""`` (shows placeholder).
 */
function plainToHtml(text: string): string {
  if (!text.trim()) return "";
  const esc = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return text
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter((b) => b.length > 0)
    .map((b) => `<p>${esc(b).replace(/\r?\n/g, "<br>")}</p>`)
    .join("");
}

export const PEC_PREVIEW_OWNED_FIELDS = [
  "provider",
  "from_pec_address",
  "to",
  "cc",
  "subject",
  "body",
] as const;

type PecFieldName = (typeof PEC_PREVIEW_OWNED_FIELDS)[number];

export interface PecSendPreviewProps {
  /** Proposal id — required for the inline AI refiner (refine-field BE). */
  proposalId?: string;
  /** Host-supplied transport for the inline AI refiner (body). */
  refineSubmit?: (input: RefineSubmitInput) => Promise<RefineSubmitResult>;
  /** OPTIONAL diff-mode transport (Jul 2026) — Apple-style inline redline for
   * the body rewrite; takes precedence over ``refineSubmit`` when present,
   * which stays wired as the replace-in-place fallback. */
  refineSubmitWithDiff?: RefineSubmitWithDiff;
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Provider label fallback — "Aruba PEC" / "InfoCert PEC" (read-only). */
  readonly providerLabel?: string;
  /** Controlled API — present when the proposal is still editable. */
  schema?: readonly ActionFieldSpec[];
  values?: Readonly<Record<string, ActionFieldValue>>;
  errors?: Readonly<Record<string, ActionFieldError | undefined>>;
  onChange?: (next: Record<string, ActionFieldValue>) => void;
  disabled?: boolean;
  className?: string;
}

/** Comma/semicolon-separated string or array → list of trimmed addresses. */
function asAddressList(v: ActionFieldValue | undefined): readonly string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === "string");
  if (typeof v === "string" && v.length > 0) {
    return v.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export const PecSendPreview: React.FC<PecSendPreviewProps> = ({
  proposalId,
  refineSubmit,
  refineSubmitWithDiff,
  proposedFields,
  executedFields,
  providerLabel,
  schema,
  values,
  errors,
  onChange,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  const isControlled = Boolean(values && onChange);
  const display = executedFields ?? proposedFields;
  const liveValues = values ?? display;

  const providerSpec = specOfName(schema, "provider");
  const fromSpec = specOfName(schema, "from_pec_address");
  const toSpec = specOfName(schema, "to");
  const ccSpec = specOfName(schema, "cc");
  const subjectSpec = specOfName(schema, "subject");
  const bodySpec = specOfName(schema, "body");

  const commit = useEnvelopeCommit<PecFieldName>(values, onChange);

  // -------------------------------------------------------------------------
  // Read-only fallback — post-execution snapshot (no editable inputs).
  // -------------------------------------------------------------------------
  if (!isControlled) {
    const to = asRecipientText(display.to) || asString(display.recipient);
    const cc = asRecipientText(display.cc);
    const subject = asString(display.subject);
    const body = asString(display.body) || asString(display.body_text);
    const attachments = asAddressList(
      display.attachments ?? display.attachment_names,
    );

    return (
      <div className={cn("flex flex-col gap-2 text-sm", className)}>
        <EnvelopeHeader
          icon={<Shield className="h-3.5 w-3.5" />}
          label={providerLabel ?? i18n._(msg`PEC certificata`)}
        />
        <div className={ENVELOPE_CLASSES.cardReadonly}>
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-ink-3">{i18n._(msg`A (PEC):`)}</dt>
            <dd className="break-all font-medium font-mono text-ink-1">
              {to || "—"}
            </dd>
            {cc ? (
              <>
                <dt className="text-ink-3">{i18n._(msg`Cc:`)}</dt>
                <dd className="truncate text-ink-1">{cc}</dd>
              </>
            ) : null}
            <dt className="text-ink-3">{i18n._(msg`Oggetto:`)}</dt>
            <dd className="truncate font-medium text-ink-1">{subject || "—"}</dd>
          </dl>
          {body ? (
            <div className="mt-2 max-h-[280px] overflow-y-auto whitespace-pre-wrap border-hairline border-t pt-2 text-ink-1 text-xs leading-relaxed">
              {body}
            </div>
          ) : null}
          {attachments.length > 0 ? (
            <ul className="mt-2 flex flex-col gap-1 border-hairline border-t pt-2">
              {attachments.map((a) => (
                <li
                  key={a}
                  className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-1"
                >
                  <Paperclip className="h-3 w-3 shrink-0 text-ink-3" aria-hidden />
                  <span className="truncate">{a}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Controlled — certified envelope editor.
  // -------------------------------------------------------------------------
  const providerValue = asString(liveValues.provider) || "aruba";
  const fromValue = asString(liveValues.from_pec_address);
  const toList = asAddressList(liveValues.to);
  const ccList = asAddressList(liveValues.cc);
  const subjectValue = asString(liveValues.subject);
  const bodyHtml = plainToHtml(asString(liveValues.body));

  const providerOptions =
    providerSpec?.options ?? [
      { value: "aruba", label: "Aruba" },
      { value: "infocert", label: "InfoCert" },
    ];
  const fromOptions = fromSpec?.options ?? [];
  const showCc = ccSpec !== undefined || ccList.length > 0;

  const toHelp =
    toSpec?.helperText ??
    i18n._(msg`Indirizzo PEC del destinatario — verifica con attenzione.`);

  // ✨ / ⌘K body AI-edit — adapt the host's diff-mode refine transport to the
  // sheet's ``aiEdit`` shape (falls back to wrapping the legacy replace-in-place
  // ``refineSubmit`` as one whole-text change). Body stays PLAIN TEXT on the
  // wire — only ``body`` is committed.
  const aiEdit = React.useMemo<RichEmailAiEdit | undefined>(() => {
    if (!proposalId || disabled || bodySpec?.readonly) return undefined;
    if (refineSubmitWithDiff) {
      return async ({ text, instruction }) => {
        const r = await refineSubmitWithDiff({
          proposalId,
          fieldName: "body",
          fullText: text,
          selectionStart: 0,
          selectionEnd: text.length,
          instruction,
        });
        return { original: r.original, rewritten: r.rewritten, changes: r.changes };
      };
    }
    if (refineSubmit) {
      return async ({ text, instruction }) => {
        const r = await refineSubmit({
          proposalId,
          fieldName: "body",
          fullText: text,
          selectionStart: 0,
          selectionEnd: text.length,
          instruction,
        });
        const rewritten = r.success && r.newFullText != null ? r.newFullText : text;
        return {
          original: text,
          rewritten,
          changes:
            rewritten === text
              ? []
              : [{ id: "0", type: "replace" as const, before: text, after: rewritten }],
        };
      };
    }
    return undefined;
  }, [proposalId, disabled, bodySpec?.readonly, refineSubmitWithDiff, refineSubmit]);

  // Header — legal banner · Gestore · Da (casella PEC mittente). The Da row's
  // own border-b is dropped: the sheet's header wrapper supplies the divider
  // below the header block.
  const header = (
    <div>
      {/* Legal-weight warning — non color-only (icon + text), leading
          compliance row (PEC ha valore legale, non annullabile). */}
      <div
        role="note"
        className="flex items-start gap-2 border-hairline border-b bg-amber-50/70 px-3 py-2 text-[12px] text-amber-900 dark:bg-amber-500/10 dark:text-amber-200"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <span>
          <strong className="font-semibold">{i18n._(msg`Comunicazione con valore legale.`)}</strong>{" "}
          {i18n._(msg`La PEC viene firmata e archiviata 10 anni e`)}{" "}
          <strong>{i18n._(msg`non può essere annullata`)}</strong>
          {i18n._(msg`: verifica il destinatario prima di inviare.`)}
        </span>
      </div>

      {/* Gestore PEC. */}
      <div>
        <div className="flex items-center gap-1.5 border-hairline border-b px-3 py-2">
          <span className={FIELD_LABEL}>{i18n._(msg`Gestore`)}</span>
          <StyledSelect
            className={SHEET_CONTROL}
            value={providerValue}
            onChange={(e) => commit("provider", e.target.value)}
            disabled={disabled || providerSpec?.readonly}
            aria-label={i18n._(msg`Gestore PEC`)}
          >
            {providerOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </StyledSelect>
        </div>
        {errors?.provider ? (
          <p className={SHEET_ERR}>{errors.provider.message}</p>
        ) : providerSpec?.helperText ? (
          <p className={SHEET_HELP}>{providerSpec.helperText}</p>
        ) : null}
      </div>

      {/* Da — casella PEC mittente. */}
      <div>
        <div className="flex items-center gap-1.5 px-3 py-2">
          <span className={FIELD_LABEL}>{i18n._(msg`Da`)}</span>
          {fromOptions.length > 0 ? (
            <StyledSelect
              className={SHEET_CONTROL}
              value={fromValue}
              onChange={(e) => commit("from_pec_address", e.target.value)}
              disabled={disabled || fromSpec?.readonly}
              aria-label={i18n._(msg`Casella PEC mittente`)}
            >
              <option value="">{i18n._(msg`— Predefinita —`)}</option>
              {fromOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </StyledSelect>
          ) : (
            <input
              type="email"
              className={SHEET_CONTROL}
              value={fromValue}
              onChange={(e) => commit("from_pec_address", e.target.value)}
              disabled={disabled || fromSpec?.readonly}
              placeholder={i18n._(msg`azienda@pec.example.it`)}
              aria-label={i18n._(msg`Casella PEC mittente`)}
            />
          )}
        </div>
        {errors?.from_pec_address ? (
          <p className={SHEET_ERR}>{errors.from_pec_address.message}</p>
        ) : fromSpec?.helperText ? (
          <p className={SHEET_HELP}>{fromSpec.helperText}</p>
        ) : null}
      </div>
    </div>
  );

  return (
    <div className={cn("flex flex-col gap-2 text-sm", className)}>
      <EnvelopeHeader
        icon={<Shield className="h-3.5 w-3.5" />}
        label={i18n._(msg`PEC certificata`)}
      />

      {/* THE LETTER — the ONE shared compose sheet (rich body: toolbar,
          selection AI-edit, ghost-text). PEC keeps its legal banner + certified
          sender rows in the header; recipients are certified PEC addresses, so
          they stay free-text chips (no People autocomplete transport). */}
      <EmailComposeSheet
        header={header}
        to={toList}
        onToChange={(next) => commit("to", next.join(", "))}
        toError={errors?.to?.message}
        toHelp={errors?.to ? undefined : toHelp}
        toLocked={disabled || toSpec?.readonly}
        toPlaceholder={i18n._(msg`destinatario@pec.example.it`)}
        cc={ccList}
        onCcChange={(next) => commit("cc", next.join(", "))}
        showCc={showCc}
        ccError={errors?.cc?.message}
        ccHelp={ccSpec?.helperText}
        ccLocked={disabled || ccSpec?.readonly}
        subject={subjectValue}
        onSubjectChange={(v) => commit("subject", v)}
        subjectLocked={disabled || subjectSpec?.readonly}
        subjectPlaceholder={i18n._(msg`Oggetto della PEC`)}
        bodyHtml={bodyHtml}
        onBodyChange={(_html, text) => commit("body", text)}
        bodyPlaceholder={i18n._(msg`Scrivi qui il corpo della PEC…`)}
        bodyDisabled={disabled || bodySpec?.readonly}
        aiEdit={aiEdit}
      />
      {/* Subject / body validation + helper text — the shared sheet renders the
          inputs but not these action-schema messages, so they surface here. */}
      {errors?.subject ? (
        <p className={SHEET_ERR}>{errors.subject.message}</p>
      ) : subjectSpec?.helperText ? (
        <p className={SHEET_HELP}>{subjectSpec.helperText}</p>
      ) : null}
      {errors?.body ? (
        <p className={SHEET_ERR}>{errors.body.message}</p>
      ) : bodySpec?.helperText ? (
        <p className={SHEET_HELP}>{bodySpec.helperText}</p>
      ) : null}
    </div>
  );
};
PecSendPreview.displayName = "PecSendPreview";
